import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function Internetfuehrerschein() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [examSlots, setExamSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [message, setMessage] = useState(null);
  const [view, setView] = useState('overview'); // 'overview', 'quiz', 'exams', 'certificates'

  const isLoggedIn = !!user?.token;

  // Fetch data
  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, certRes, examRes] = await Promise.all([
        fetch('/api/quiz/categories', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch('/api/certificates', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch('/api/exams/slots', {
          headers: { 'Authorization': `Bearer ${user.token}` }
        })
      ]);

      const catData = await catRes.json();
      const certData = await certRes.json();
      const examData = await examRes.json();

      setCategories(Array.isArray(catData) ? catData : []);
      setCertificates(Array.isArray(certData) ? certData : []);
      setExamSlots(Array.isArray(examData) ? examData : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  // Start a quiz
  const startQuiz = async (category) => {
    try {
      const res = await fetch(`/api/quiz/categories/${category.id}/questions`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const questions = await res.json();
      setQuizQuestions(questions);
      setQuizAnswers({});
      setQuizResult(null);
      setActiveQuiz(category);
      setView('quiz');
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Laden des Quiz' });
    }
  };

  // Submit quiz
  const submitQuiz = async () => {
    try {
      const res = await fetch('/api/quiz/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          category_id: activeQuiz.id,
          answers: quizAnswers
        })
      });
      const result = await res.json();
      setQuizResult(result);
      fetchData(); // Refresh progress
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Absenden des Quiz' });
    }
  };

  // Book exam
  const bookExam = async (slotId) => {
    const childName = user.name || 'Kind';
    try {
      const res = await fetch('/api/exams/book', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          exam_slot_id: slotId,
          child_name: childName
        })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Pruefung erfolgreich gebucht!' });
        fetchData();
      } else {
        setMessage({ type: 'danger', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler bei der Buchung' });
    }
  };

  // Cancel exam booking
  const cancelExamBooking = async (bookingId) => {
    if (!window.confirm('Moechtest du diese Buchung wirklich stornieren?')) return;
    try {
      await fetch(`/api/exams/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setMessage({ type: 'success', text: 'Buchung storniert' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Fehler beim Stornieren' });
    }
  };

  // Print certificate
  const printCertificate = (cert) => {
    const printWindow = window.open('', '_blank');
    const isKlein = cert.slug === 'klein';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${cert.name}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Open+Sans&display=swap');

          * { margin: 0; padding: 0; box-sizing: border-box; }

          body {
            font-family: 'Open Sans', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }

          .certificate {
            background: white;
            width: 800px;
            padding: 60px;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            text-align: center;
            position: relative;
            overflow: hidden;
          }

          .certificate::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 10px;
            background: ${isKlein ? 'linear-gradient(90deg, #3b82f6, #8b5cf6)' : 'linear-gradient(90deg, #f59e0b, #ef4444)'};
          }

          .badge-icon {
            font-size: 80px;
            margin-bottom: 20px;
          }

          .title {
            font-family: 'Playfair Display', serif;
            font-size: 36px;
            color: #1f2937;
            margin-bottom: 10px;
          }

          .subtitle {
            color: #6b7280;
            font-size: 18px;
            margin-bottom: 40px;
          }

          .awarded-to {
            color: #6b7280;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 10px;
          }

          .name {
            font-family: 'Playfair Display', serif;
            font-size: 42px;
            color: ${isKlein ? '#3b82f6' : '#f59e0b'};
            margin-bottom: 30px;
          }

          .description {
            color: #4b5563;
            font-size: 16px;
            max-width: 500px;
            margin: 0 auto 40px;
            line-height: 1.6;
          }

          .topics {
            display: flex;
            justify-content: center;
            gap: 15px;
            flex-wrap: wrap;
            margin-bottom: 40px;
          }

          .topic {
            background: ${isKlein ? '#eff6ff' : '#fef3c7'};
            color: ${isKlein ? '#1d4ed8' : '#b45309'};
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 14px;
          }

          .footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 40px;
            padding-top: 30px;
            border-top: 2px solid #e5e7eb;
          }

          .date-section, .number-section {
            text-align: left;
          }

          .number-section {
            text-align: right;
          }

          .label {
            color: #9ca3af;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .value {
            color: #1f2937;
            font-size: 16px;
            font-weight: 600;
          }

          .logo-section {
            text-align: center;
          }

          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #6366f1;
          }

          @media print {
            body { background: white; padding: 0; }
            .certificate { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="badge-icon">${isKlein ? '🎓' : '🏆'}</div>
          <h1 class="title">${cert.name}</h1>
          <p class="subtitle">Zertifikat fuer Medienkompetenz</p>

          <p class="awarded-to">Verliehen an</p>
          <p class="name">${user.name || 'Teilnehmer'}</p>

          <p class="description">
            ${isKlein
              ? 'fuer das erfolgreiche Bestehen der Pruefung zu den Grundlagen der Internetsicherheit und den verantwortungsvollen Umgang mit digitalen Medien.'
              : 'fuer das erfolgreiche Bestehen der erweiterten Pruefung zu Medienkompetenz, kritischem Denken und digitalem Engagement.'}
          </p>

          <div class="topics">
            ${isKlein
              ? '<span class="topic">🔑 Passwoerter</span><span class="topic">🛡️ Datenschutz</span><span class="topic">👤 Fremde im Netz</span><span class="topic">🆘 Hilfe holen</span>'
              : '<span class="topic">📰 Fake News</span><span class="topic">👣 Digitaler Fussabdruck</span><span class="topic">🤝 Cybermobbing</span><span class="topic">©️ Urheberrecht</span>'}
          </div>

          <div class="footer">
            <div class="date-section">
              <p class="label">Datum</p>
              <p class="value">${new Date(cert.earned_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
            <div class="logo-section">
              <p class="logo">Futurhinos for Kids</p>
            </div>
            <div class="number-section">
              <p class="label">Zertifikat-Nr.</p>
              <p class="value">${cert.certificate_number}</p>
            </div>
          </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Not logged in view
  if (!isLoggedIn) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <h2 className="mb-4">Internetfuehrerschein</h2>
          <div className="mb-4" style={{ fontSize: '4rem' }}>🎓</div>
          <p className="lead mb-4">
            Teste dein Wissen und werde zum Internet-Profi!
          </p>
          <div className="row justify-content-center mb-4">
            <div className="col-md-5">
              <div className="card bg-light">
                <div className="card-body">
                  <h5>🎓 Kleiner Internetfuehrerschein</h5>
                  <p className="small text-muted mb-0">
                    Passwoerter, Datenschutz, Fremde im Netz, Hilfe holen
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="card bg-light">
                <div className="card-body">
                  <h5>🏆 Grosser Internetfuehrerschein</h5>
                  <p className="small text-muted mb-0">
                    Fake News, Digitaler Fussabdruck, Cybermobbing, Urheberrecht
                  </p>
                </div>
              </div>
            </div>
          </div>
          <a href="/login" className="btn btn-primary btn-lg me-3">
            Anmelden um zu starten
          </a>
          <a
            href="/internetfuehrerschein/quiz-zum-ueben.html"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-lg"
          >
            🧠 Quiz zum Ueben
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border"></div></div>;
  }

  // Quiz view
  if (view === 'quiz' && activeQuiz) {
    const allAnswered = quizQuestions.length > 0 &&
      quizQuestions.every(q => quizAnswers[q.id] !== undefined);

    return (
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            {activeQuiz.emoji} {activeQuiz.name}
          </h5>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setView('overview')}>
            Zurueck
          </button>
        </div>
        <div className="card-body">
          {quizResult ? (
            <div className="text-center py-4">
              <div style={{ fontSize: '4rem' }} className="mb-3">
                {quizResult.passed ? '🎉' : '💪'}
              </div>
              <h3 className={quizResult.passed ? 'text-success' : 'text-warning'}>
                {quizResult.passed ? 'Super! Bestanden!' : 'Noch nicht ganz...'}
              </h3>
              <p className="lead">
                Du hast {quizResult.score} von {quizResult.totalQuestions} Fragen richtig beantwortet.
              </p>
              {!quizResult.passed && (
                <p className="text-muted">
                  Du brauchst mindestens 70% um zu bestehen. Versuche es noch einmal!
                </p>
              )}
              <div className="mt-4">
                <button className="btn btn-primary me-2" onClick={() => startQuiz(activeQuiz)}>
                  Nochmal versuchen
                </button>
                <button className="btn btn-outline-secondary" onClick={() => setView('overview')}>
                  Zurueck zur Uebersicht
                </button>
              </div>
            </div>
          ) : (
            <>
              {quizQuestions.length === 0 ? (
                <div className="alert alert-info">
                  Fuer dieses Thema gibt es noch keine Fragen. Schau spaeter nochmal vorbei!
                </div>
              ) : (
                <>
                  {quizQuestions.map((q, idx) => (
                    <div key={q.id} className="card mb-3">
                      <div className="card-body">
                        <h6 className="card-title">Frage {idx + 1}</h6>
                        <p className="mb-3">{q.question}</p>
                        <div className="d-flex flex-column gap-2">
                          {(q.answers || []).map((answer, ansIdx) => (
                            <button
                              key={ansIdx}
                              className={`btn text-start ${quizAnswers[q.id] === ansIdx ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: ansIdx })}
                            >
                              {answer}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="text-center mt-4">
                    <button
                      className="btn btn-success btn-lg"
                      disabled={!allAnswered}
                      onClick={submitQuiz}
                    >
                      Quiz abschicken
                    </button>
                    {!allAnswered && (
                      <p className="text-muted mt-2">
                        Beantworte alle Fragen um fortzufahren
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Calculate progress
  const kleinCategories = categories.filter(c => c.license_type === 'klein');
  const grossCategories = categories.filter(c => c.license_type === 'gross');
  const kleinPassed = kleinCategories.filter(c => c.last_passed).length;
  const grossPassed = grossCategories.filter(c => c.last_passed).length;
  const kleinCert = certificates.find(c => c.slug === 'klein');
  const grossCert = certificates.find(c => c.slug === 'gross');

  return (
    <div>
      {message && (
        <div className={`alert alert-${message.type} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Header */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h2 className="mb-3">🎓 Internetfuehrerschein</h2>
              <p className="lead mb-0">
                Teste dein Wissen mit unseren Quizzen und verdiene dir deinen Internetfuehrerschein!
              </p>
            </div>
            <div className="col-md-4 text-end">
              {kleinCert?.earned && (
                <span className="badge bg-success fs-6 me-2">🎓 Kleiner FS</span>
              )}
              {grossCert?.earned && (
                <span className="badge bg-warning text-dark fs-6">🏆 Grosser FS</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Kleiner Internetfuehrerschein - Externe App */}
      {user?.internetfuehrerschein_enabled ? (
        <div className="card mb-4 border-success">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h5 className="mb-2">🎓 Kleiner Internetfuehrerschein</h5>
                <p className="text-muted mb-0">
                  Du bist freigeschaltet! Starte jetzt den kleinen Internetfuehrerschein und lerne alles ueber sicheres Surfen im Internet.
                </p>
              </div>
              <div className="col-md-4 text-end">
                <a
                  href="https://internetfuhrerschein-production.up.railway.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-lg"
                >
                  Jetzt starten
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card mb-4 border-secondary">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h5 className="mb-2">🎓 Kleiner Internetfuehrerschein</h5>
                <p className="text-muted mb-0">
                  Der kleine Internetfuehrerschein ist noch nicht fuer dich freigeschaltet. Sprich mit deinem Kursleiter!
                </p>
              </div>
              <div className="col-md-4 text-end">
                <button className="btn btn-secondary btn-lg" disabled>
                  Nicht freigeschaltet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz zum Ueben */}
      <div className="card mb-4 border-primary">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h5 className="mb-2">🧠 Quiz zum Ueben</h5>
              <p className="text-muted mb-0">
                20 knifflige Fragen zu KI, Fake News, Passwoertern, Datenschutz und Urheberrecht - perfekt zur Vorbereitung!
              </p>
            </div>
            <div className="col-md-4 text-end">
              <a
                href="/internetfuehrerschein/quiz-zum-ueben.html"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Quiz starten
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${view === 'overview' ? 'active' : ''}`}
            onClick={() => setView('overview')}
          >
            Uebersicht
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${view === 'exams' ? 'active' : ''}`}
            onClick={() => setView('exams')}
          >
            Pruefungstermine
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${view === 'certificates' ? 'active' : ''}`}
            onClick={() => setView('certificates')}
          >
            Meine Zertifikate
          </button>
        </li>
      </ul>

      {/* Overview Tab */}
      {view === 'overview' && (
        <>
          {/* Kleiner Fuehrerschein */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">🎓 Kleiner Internetfuehrerschein</h5>
              <span className="badge bg-primary">{kleinPassed}/{kleinCategories.length} bestanden</span>
            </div>
            <div className="card-body">
              <div className="progress mb-3" style={{ height: '20px' }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(kleinPassed / Math.max(kleinCategories.length, 1)) * 100}%` }}
                >
                  {Math.round((kleinPassed / Math.max(kleinCategories.length, 1)) * 100)}%
                </div>
              </div>
              <div className="row g-3">
                {kleinCategories.map(cat => (
                  <div key={cat.id} className="col-md-6 col-lg-3">
                    <div className={`card h-100 ${cat.last_passed ? 'border-success' : ''}`}>
                      <div className="card-body text-center">
                        <div style={{ fontSize: '2rem' }}>{cat.emoji}</div>
                        <h6 className="card-title">{cat.name}</h6>
                        <p className="card-text small text-muted">{cat.description}</p>
                        {cat.last_passed ? (
                          <span className="badge bg-success">Bestanden</span>
                        ) : cat.best_score !== null ? (
                          <span className="badge bg-warning text-dark">
                            Bisher: {cat.best_score}/{cat.attempted_questions}
                          </span>
                        ) : null}
                      </div>
                      <div className="card-footer bg-transparent border-0 text-center pb-3">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => startQuiz(cat)}
                          disabled={cat.question_count === 0}
                        >
                          {cat.last_passed ? 'Nochmal ueben' : 'Quiz starten'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {kleinPassed >= kleinCategories.length && kleinCategories.length > 0 && !kleinCert?.earned && (
                <div className="alert alert-success mt-3 mb-0">
                  <strong>Super!</strong> Du hast alle Themen bestanden! Du kannst jetzt die Pruefung fuer den kleinen Internetfuehrerschein buchen.
                  <button className="btn btn-success btn-sm ms-3" onClick={() => setView('exams')}>
                    Pruefung buchen
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Grosser Fuehrerschein */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">🏆 Grosser Internetfuehrerschein</h5>
              <span className="badge bg-warning text-dark">{grossPassed}/{grossCategories.length} bestanden</span>
            </div>
            <div className="card-body">
              {!kleinCert?.earned ? (
                <div className="alert alert-info mb-0">
                  Absolviere zuerst den kleinen Internetfuehrerschein, um den grossen freizuschalten!
                </div>
              ) : (
                <>
                  <div className="progress mb-3" style={{ height: '20px' }}>
                    <div
                      className="progress-bar bg-warning"
                      style={{ width: `${(grossPassed / Math.max(grossCategories.length, 1)) * 100}%` }}
                    >
                      {Math.round((grossPassed / Math.max(grossCategories.length, 1)) * 100)}%
                    </div>
                  </div>
                  <div className="row g-3">
                    {grossCategories.map(cat => (
                      <div key={cat.id} className="col-md-6 col-lg-3">
                        <div className={`card h-100 ${cat.last_passed ? 'border-success' : ''}`}>
                          <div className="card-body text-center">
                            <div style={{ fontSize: '2rem' }}>{cat.emoji}</div>
                            <h6 className="card-title">{cat.name}</h6>
                            <p className="card-text small text-muted">{cat.description}</p>
                            {cat.last_passed ? (
                              <span className="badge bg-success">Bestanden</span>
                            ) : cat.best_score !== null ? (
                              <span className="badge bg-warning text-dark">
                                Bisher: {cat.best_score}/{cat.attempted_questions}
                              </span>
                            ) : null}
                          </div>
                          <div className="card-footer bg-transparent border-0 text-center pb-3">
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => startQuiz(cat)}
                              disabled={cat.question_count === 0}
                            >
                              {cat.last_passed ? 'Nochmal ueben' : 'Quiz starten'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {grossPassed >= grossCategories.length && grossCategories.length > 0 && !grossCert?.earned && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <strong>Fantastisch!</strong> Du hast alle Themen bestanden! Du kannst jetzt die Pruefung fuer den grossen Internetfuehrerschein buchen.
                      <button className="btn btn-warning btn-sm ms-3" onClick={() => setView('exams')}>
                        Pruefung buchen
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Exams Tab */}
      {view === 'exams' && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Pruefungstermine</h5>
          </div>
          <div className="card-body">
            {examSlots.length === 0 ? (
              <div className="alert alert-info mb-0">
                Aktuell sind keine Pruefungstermine verfuegbar. Schau spaeter nochmal vorbei!
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Uhrzeit</th>
                      <th>Fuehrerschein</th>
                      <th>Plaetze</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {examSlots.map(slot => {
                      const available = slot.max_participants - slot.booked_count;
                      const cert = certificates.find(c => c.id === slot.certificate_id);
                      const canBook = cert?.ready_for_exam && !cert?.earned && available > 0 && !slot.my_booking_id;

                      return (
                        <tr key={slot.id}>
                          <td>{new Date(slot.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                          <td>{slot.time} Uhr</td>
                          <td>
                            <span className={`badge ${slot.certificate_id === 1 ? 'bg-primary' : 'bg-warning text-dark'}`}>
                              {slot.certificate_name}
                            </span>
                          </td>
                          <td>
                            {available > 0 ? (
                              <span className="text-success">{available} frei</span>
                            ) : (
                              <span className="text-danger">ausgebucht</span>
                            )}
                          </td>
                          <td>
                            {slot.my_booking_id ? (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => cancelExamBooking(slot.my_booking_id)}
                              >
                                Stornieren
                              </button>
                            ) : canBook ? (
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => bookExam(slot.id)}
                              >
                                Buchen
                              </button>
                            ) : !cert?.ready_for_exam && !cert?.earned ? (
                              <span className="text-muted small">Erst alle Quizze bestehen</span>
                            ) : cert?.earned ? (
                              <span className="badge bg-success">Bereits bestanden</span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Certificates Tab */}
      {view === 'certificates' && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Meine Zertifikate</h5>
          </div>
          <div className="card-body">
            {certificates.filter(c => c.earned).length === 0 ? (
              <div className="text-center py-5">
                <div style={{ fontSize: '4rem' }} className="mb-3">📜</div>
                <h5>Noch keine Zertifikate</h5>
                <p className="text-muted">
                  Bestehe die Quizze und lege die Pruefung ab, um dein erstes Zertifikat zu erhalten!
                </p>
                <button className="btn btn-primary" onClick={() => setView('overview')}>
                  Zu den Quizzen
                </button>
              </div>
            ) : (
              <div className="row g-4">
                {certificates.filter(c => c.earned).map(cert => (
                  <div key={cert.id} className="col-md-6">
                    <div className="card border-success">
                      <div className="card-body text-center py-4">
                        <div style={{ fontSize: '4rem' }} className="mb-2">{cert.icon}</div>
                        <h4>{cert.name}</h4>
                        <p className="text-muted">{cert.description}</p>
                        <p className="mb-3">
                          <small>Bestanden am: {new Date(cert.earned_date).toLocaleDateString('de-DE')}</small>
                          <br />
                          <small className="text-muted">Nr. {cert.certificate_number}</small>
                        </p>
                        <button
                          className="btn btn-outline-success"
                          onClick={() => printCertificate(cert)}
                        >
                          Zertifikat drucken
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Internetfuehrerschein;
